import React, { useEffect, useRef, useState } from 'react';

import { editInvoice, submitInvoice } from '../../../api_handlers/invoice';
import { withReturnTo } from '../../invoices/lib/returnTo';
import { instructionPercent } from '../../invoices/lib/splits';
import { duplicateInvoice, submitErrorMessage } from './lib/submitError';
import {
  Alert,
  BrandMark,
  ICONS,
  Icon,
  KIT_CSS,
  Modal,
  money,
  shortDate,
  useStyleSheet,
} from '../../invoices/kit';

const ACCEPTED = '.pdf,.png,.jpg,.jpeg';
const MAX_FILE_BYTES = 10 * 1024 * 1024;

const CSS = `
.ivf-campaign { display: flex; gap: 13px; align-items: center; padding: 14px 16px; background: var(--canvas); border-radius: var(--radius-sm); margin-bottom: 22px; }
.ivf-campaign__name { font-size: 14.5px; font-weight: 800; color: var(--ink); margin: 0; }
.ivf-campaign__meta { font-size: 12.5px; color: var(--ink-3); margin: 2px 0 0; }
.ivf-campaign__agreed { margin-left: auto; text-align: right; flex: none; }
.ivf-campaign__agreedLabel { font-size: 10.5px; font-weight: 800; letter-spacing: .06em; text-transform: uppercase; color: var(--ink-4); margin: 0 0 3px; }
.ivf-campaign__agreedValue { font-size: 16px; font-weight: 800; color: var(--ink); letter-spacing: -.01em; }

.ivf-payout { display: flex; gap: 14px; align-items: center; justify-content: space-between; padding: 14px 16px; margin-bottom: 20px; border-radius: var(--radius-sm); background: #FFFBEB; border: 1px solid #FDE9B5; }
.ivf-payout__title { font-size: 14px; font-weight: 800; color: #92400E; margin: 0 0 3px; }
.ivf-payout__text { font-size: 12.5px; line-height: 1.5; color: #78350F; margin: 0; }
@media (max-width: 560px) { .ivf-payout { flex-direction: column; align-items: flex-start; } }
.ivf-rows { display: grid; gap: 18px; }
.ivf-pair { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
.ivf-amount { position: relative; }
.ivf-amount .iv-input { padding-left: 30px; font-size: 19px; font-weight: 800; letter-spacing: -.01em; }
.ivf-amount__rupee { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); font-size: 17px; font-weight: 800; color: var(--ink-4); pointer-events: none; }

.ivf-nudge { display: flex; align-items: center; gap: 8px; margin-top: 8px; font-size: 12.5px; color: #B45309; background: #FFFBEB; border: 1px solid #FDE9B5; border-radius: 8px; padding: 8px 11px; }
.ivf-nudge button { background: none; border: 0; padding: 0; font: inherit; font-weight: 800; color: #92400E; cursor: pointer; text-decoration: underline; }

.ivf-drop {
  border: 1.5px dashed var(--line-2); border-radius: var(--radius); background: #FCFDFE;
  padding: 26px 20px; text-align: center; cursor: pointer; transition: border-color .16s ease, background .16s ease;
}
.ivf-drop:hover { border-color: var(--accent); background: var(--accent-bg); }
.ivf-drop--over { border-color: var(--accent); background: var(--accent-bg); border-style: solid; }
.ivf-drop--bad { border-color: #DC2626; }
.ivf-drop__icon { width: 40px; height: 40px; margin: 0 auto 12px; border-radius: 11px; background: var(--accent-bg); color: var(--accent-ink); display: grid; place-items: center; }
.ivf-drop__title { font-size: 14px; font-weight: 800; color: var(--ink); margin: 0 0 4px; }
.ivf-drop__hint { font-size: 12.5px; color: var(--ink-3); margin: 0; }

.ivf-file { display: flex; align-items: center; gap: 12px; padding: 13px 15px; border: 1px solid var(--line); border-radius: var(--radius-sm); background: var(--surface); }
.ivf-file__icon { width: 34px; height: 34px; border-radius: 9px; background: var(--accent-bg); color: var(--accent-ink); display: grid; place-items: center; flex: none; }
.ivf-file__name { font-size: 13.5px; font-weight: 800; color: var(--ink); margin: 0; word-break: break-all; }
.ivf-file__size { font-size: 12px; color: var(--ink-4); margin: 2px 0 0; }

.ivf-posts { display: grid; gap: 6px; margin-top: 6px; }
.ivf-post { display: flex; align-items: center; gap: 9px; padding: 9px 12px; border: 1px solid var(--line); border-radius: var(--radius-sm); background: var(--surface); font-size: 13px; color: var(--ink); text-decoration: none; min-width: 0; }
.ivf-post:hover { border-color: var(--accent); color: var(--accent-ink); }
.ivf-post__dot { width: 8px; height: 8px; border-radius: 50%; flex: none; background: #9CA3AF; }
.ivf-post__dot--instagram { background: #E1306C; }
.ivf-post__dot--youtube { background: #FF0000; }
.ivf-post__label { flex: 1; min-width: 0; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; font-weight: 700; }
.ivf-post__when { font-size: 12px; color: var(--ink-4); flex: none; }
.ivf-posts__more { font-size: 12px; color: var(--ink-4); margin: 2px 0 0; }

@media (max-width: 560px) { .ivf-pair { grid-template-columns: 1fr; } }
`;

function fieldError(errors, key) {
  const value = errors[key];
  if (!value) return '';
  return Array.isArray(value) ? value.join(' ') : String(value);
}

function formatBytes(bytes) {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

function Field({ label, error, hint, children }) {
  return (
    <label className="iv-field">
      <span className="iv-label">{label}</span>
      {children}
      {error ? <span className="iv-hint iv-hint--error">{error}</span> : null}
      {!error && hint ? <span className="iv-hint">{hint}</span> : null}
    </label>
  );
}

/**
 * Invoice submission form for a single campaign.
 * `campaign` comes from the eligible-campaigns endpoint; passing `invoice`
 * instead switches to editing an existing submission, where the file is
 * optional because one is already on record.
 */
export default function InvoiceForm({ open, campaign, invoice, onClose, onSubmitted }) {
  useStyleSheet('iv-kit', KIT_CSS);
  useStyleSheet('ivf-form', CSS);

  const isEdit = Boolean(invoice);
  const [values, setValues] = useState({
    amount: '',
    invoice_number: '',
    contact_phone: '',
    contact_email: '',
    post_url: '',
  });
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [needsPayout, setNeedsPayout] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setValues({
        amount: invoice.amount ? String(invoice.amount) : '',
        invoice_number: invoice.invoice_number || '',
        contact_phone: invoice.contact_phone || '',
        contact_email: invoice.contact_email || '',
        post_url: invoice.post_url || '',
      });
    } else if (campaign) {
      setValues({
        amount: campaign.agreed_amount ? String(campaign.agreed_amount) : '',
        invoice_number: '',
        contact_phone: '',
        contact_email: '',
        post_url: '',
      });
    }
    setFile(null);
    setErrors({});
    setMessage('');
    setDragging(false);
    setNeedsPayout(false);
  }, [open, campaign, invoice, isEdit]);

  // Posts we already matched to this creator on the campaign. When there are
  // none, the link is the only proof of delivery, so it becomes required.
  const matchedPosts =
    (isEdit ? invoice && invoice.campaign_posts : campaign && campaign.posts) || [];
  const detectedPosts = matchedPosts.filter((post) => post.source !== 'creator');
  const postLinkRequired = detectedPosts.length === 0;

  const handleChange = (key) => (event) => {
    setValues({ ...values, [key]: event.target.value });
    if (errors[key]) setErrors({ ...errors, [key]: null });
  };

  const acceptFile = (selected) => {
    if (!selected) return;
    const name = selected.name.toLowerCase();
    const okType = ACCEPTED.split(',').some((ext) => name.endsWith(ext));
    if (!okType) {
      setErrors({ ...errors, invoice: 'Only PDF, PNG or JPG files are accepted.' });
      return;
    }
    if (selected.size > MAX_FILE_BYTES) {
      setErrors({
        ...errors,
        invoice: `File is ${formatBytes(selected.size)} — the limit is 10 MB.`,
      });
      return;
    }
    setFile(selected);
    setErrors({ ...errors, invoice: null });
  };

  const handleFile = (event) => {
    acceptFile(event.target.files && event.target.files[0]);
    // Allow re-selecting the same file after removing it.
    event.target.value = '';
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    if (saving) return;
    acceptFile(event.dataTransfer.files && event.dataTransfer.files[0]);
  };

  const validate = () => {
    const found = {};
    if (!values.amount || Number(values.amount) <= 0) {
      found.amount = 'Enter the amount you are invoicing.';
    }
    const digits = (values.contact_phone || '').replace(/\D/g, '');
    if (digits.length < 10) {
      found.contact_phone = 'Enter a valid contact number.';
    }
    if (!/^\S+@\S+\.\S+$/.test(values.contact_email || '')) {
      found.contact_email = 'Enter a valid email address.';
    }
    if (!file && !isEdit) {
      found.invoice = 'Attach your invoice file.';
    }
    const link = (values.post_url || '').trim();
    if (link && !/^https?:\/\/\S+$/i.test(link)) {
      found.post_url = 'Enter the full link to your post, starting with https://';
    } else if (!link && postLinkRequired) {
      found.post_url = 'Add the link to the content this invoice is for.';
    }
    return found;
  };

  const handleSubmit = async () => {
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length) return;

    setSaving(true);
    setMessage('');
    try {
      // A line can carry several invoices now, so say which share of the
      // agreed payout this one is for -- the sheet's "Instruction on
      // Payment". Left out when nothing was agreed, since a share of nothing
      // means nothing.
      const agreedNow = Number(campaign && campaign.agreed_amount) || 0;
      const share = isEdit ? null : instructionPercent(values.amount, agreedNow);
      const body = share === null ? values : { ...values, payment_instruction: `${share}%` };

      const saved = isEdit
        ? await editInvoice(invoice.id, values, file)
        : await submitInvoice(campaign.id, body, file);
      onSubmitted(saved);
    } catch (error) {
      if (error.fieldErrors && error.fieldErrors.code === 'payout_details_missing') {
        setNeedsPayout(true);
        setMessage('');
        return;
      }
      // A duplicate answers with a code and no prose, and the code is not a
      // field on this form. Explain it instead of colouring an input red.
      if (duplicateInvoice(error)) {
        setErrors({});
        setMessage(submitErrorMessage(error));
        return;
      }
      setErrors(error.fieldErrors || {});
      setMessage(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (!campaign && !isEdit) return null;

  // Finance cannot pay without an account on file, and the API refuses the
  // submit; say so up front rather than after the creator fills everything
  // in. `payout_details_on_file` is undefined on older API builds, which
  // must not block anyone.
  const payoutMissing =
    !isEdit && (needsPayout || (campaign && campaign.payout_details_on_file === false));
  const priceLocked = !isEdit && campaign && campaign.price_locked;

  const source = isEdit ? invoice : campaign;
  const agreed = Number(isEdit ? invoice.agreed_amount : campaign.agreed_amount) || 0;
  const entered = Number(values.amount) || 0;
  const differs = agreed > 0 && entered > 0 && entered !== agreed;

  return (
    <Modal
      open={open}
      onClose={saving ? () => {} : onClose}
      title={isEdit ? 'Edit your invoice' : 'Raise an invoice'}
      subtitle={
        isEdit
          ? 'Changes are saved against the same invoice — the accounts team sees the update.'
          : 'We send this straight to our accounts team.'
      }
      footer={
        <>
          <button
            type="button"
            className="iv-btn iv-btn--ghost"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="button"
            className="iv-btn iv-btn--primary"
            onClick={handleSubmit}
            disabled={saving || payoutMissing}
          >
            {saving ? <span className="iv-spin" /> : null}
            {isEdit ? 'Save changes' : 'Submit invoice'}
          </button>
        </>
      }
    >
      <div className="ivf-campaign">
        <BrandMark
          name={
            (source.brand && source.brand.name) ||
            source.brand_name ||
            source.campaign_name ||
            source.name
          }
          src={source.brand && source.brand.brand_logo_url}
        />
        <div style={{ minWidth: 0 }}>
          <p className="ivf-campaign__name">{source.campaign_name || source.name}</p>
          <p className="ivf-campaign__meta">
            {(source.brand && source.brand.name) || source.brand_name || ''}
            {source.end_date
              ? ` · ${source.status === 'completed' ? 'Ended' : 'Ends'} ${shortDate(
                  source.end_date,
                )}`
              : ''}
          </p>
        </div>
        {agreed > 0 ? (
          <div className="ivf-campaign__agreed">
            <p className="ivf-campaign__agreedLabel">{priceLocked ? 'Agreed · locked' : 'Agreed'}</p>
            <div className="ivf-campaign__agreedValue iv-num">{money(agreed)}</div>
          </div>
        ) : null}
      </div>

      {payoutMissing ? (
        <div className="ivf-payout">
          <div>
            <p className="ivf-payout__title">Add your payout details first</p>
            <p className="ivf-payout__text">
              We need your bank account and PAN to pay this invoice. You only do this once —
              every invoice after this uses the same details.
            </p>
          </div>
          <a
            className="iv-btn iv-btn--primary iv-btn--sm"
            href={withReturnTo(
              '/influencer/payout-settings',
              typeof window === 'undefined'
                ? ''
                : window.location.pathname + window.location.search,
            )}
          >
            Add payout details
          </a>
        </div>
      ) : null}

      {message ? (
        <div style={{ marginBottom: 20 }}>
          <Alert>{message}</Alert>
        </div>
      ) : null}

      <div className="ivf-rows">
        <div>
          <Field label="Invoice amount" error={fieldError(errors, 'amount')}>
            <span className="ivf-amount">
              <span className="ivf-amount__rupee">₹</span>
              <input
                className={`iv-input iv-num${errors.amount ? ' iv-input--invalid' : ''}`}
                type="number"
                min="0"
                inputMode="numeric"
                value={values.amount}
                onChange={handleChange('amount')}
                placeholder="0"
              />
            </span>
          </Field>
          {differs ? (
            <div className="ivf-nudge">
              <Icon path={ICONS.alert} size={15} />
              <span>
                This differs from the agreed {money(agreed)}.{' '}
                <button
                  type="button"
                  onClick={() => setValues({ ...values, amount: String(agreed) })}
                >
                  Use agreed amount
                </button>
              </span>
            </div>
          ) : null}
        </div>

        <Field
          label="Invoice number"
          error={fieldError(errors, 'invoice_number')}
          hint="Optional — your own reference, if you use one."
        >
          <input
            className={`iv-input${errors.invoice_number ? ' iv-input--invalid' : ''}`}
            value={values.invoice_number}
            onChange={handleChange('invoice_number')}
            placeholder="e.g. INV-2026-014"
          />
        </Field>

        <div className="ivf-pair">
          <Field label="Contact number" error={fieldError(errors, 'contact_phone')}>
            <input
              className={`iv-input${errors.contact_phone ? ' iv-input--invalid' : ''}`}
              type="tel"
              value={values.contact_phone}
              onChange={handleChange('contact_phone')}
              placeholder="+91 90000 00000"
            />
          </Field>
          <Field label="Contact email" error={fieldError(errors, 'contact_email')}>
            <input
              className={`iv-input${errors.contact_email ? ' iv-input--invalid' : ''}`}
              type="email"
              value={values.contact_email}
              onChange={handleChange('contact_email')}
              placeholder="you@example.com"
            />
          </Field>
        </div>

        <div>
          <span className="iv-label">Content this invoice is for</span>
          {detectedPosts.length ? (
            <>
              <div className="ivf-posts">
                {detectedPosts.slice(0, 5).map((post) => (
                  <a
                    key={post.url}
                    className="ivf-post"
                    href={post.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <span className={`ivf-post__dot ivf-post__dot--${post.platform}`} />
                    <span className="ivf-post__label">{post.label}</span>
                    {post.posted_at ? (
                      <span className="ivf-post__when">{shortDate(post.posted_at)}</span>
                    ) : null}
                  </a>
                ))}
              </div>
              {detectedPosts.length > 5 ? (
                <p className="ivf-posts__more">+{detectedPosts.length - 5} more found</p>
              ) : null}
            </>
          ) : null}
          <input
            className={`iv-input${errors.post_url ? ' iv-input--invalid' : ''}`}
            style={{ marginTop: detectedPosts.length ? 10 : 0 }}
            type="url"
            inputMode="url"
            value={values.post_url}
            onChange={handleChange('post_url')}
            placeholder="https://www.instagram.com/p/…"
            aria-label="Post link"
          />
          {errors.post_url ? (
            <span className="iv-hint iv-hint--error">{fieldError(errors, 'post_url')}</span>
          ) : (
            <span className="iv-hint">
              {detectedPosts.length
                ? 'Optional — add a link if the post above is not the one you are billing for.'
                : 'We have not found a post from you on this campaign yet, so link the content this invoice is for.'}
            </span>
          )}
        </div>

        <div>
          <span className="iv-label">Invoice document</span>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED}
            onChange={handleFile}
            style={{ display: 'none' }}
          />

          {file ? (
            <div className="ivf-file">
              <span className="ivf-file__icon">
                <Icon path={ICONS.receipt} size={17} />
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p className="ivf-file__name">{file.name}</p>
                <p className="ivf-file__size iv-num">{formatBytes(file.size)}</p>
              </div>
              <button
                type="button"
                className="iv-x"
                onClick={() => setFile(null)}
                aria-label="Remove file"
                disabled={saving}
              >
                <Icon path={ICONS.x} size={17} />
              </button>
            </div>
          ) : (
            <div
              className={`ivf-drop${dragging ? ' ivf-drop--over' : ''}${
                errors.invoice ? ' ivf-drop--bad' : ''
              }`}
              role="button"
              tabIndex={0}
              onClick={() => fileInputRef.current && fileInputRef.current.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  if (fileInputRef.current) fileInputRef.current.click();
                }
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
            >
              <div className="ivf-drop__icon">
                <Icon path={ICONS.download} size={19} />
              </div>
              <p className="ivf-drop__title">
                {isEdit ? 'Replace the document' : 'Drop your invoice here'}
              </p>
              <p className="ivf-drop__hint">
                or click to browse · PDF, PNG or JPG · up to 10 MB
                {isEdit ? ' · leave empty to keep the current file' : ''}
              </p>
            </div>
          )}
          {errors.invoice ? (
            <span className="iv-hint iv-hint--error">{fieldError(errors, 'invoice')}</span>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
